import {
  TransportOrders,
  TransportOrdersForm,
} from "../../types/transportOrders.ts";
import {
  ForwardingOrder,
  ForwardingOrderCreate,
  ForwardingOrderUpdate,
  TransportOrder as TransportOrderFromApi,
  TransportOrderCreate,
  TransportOrderUpdate,
  Contract as ContractFromApi,
  ContractCreate,
  ContractUpdate,
  TransportOrderCreateWithSalesContract,
  Delivery as DeliveryFromApi,
  DeliveryUpdate,
} from "../../api";
import { isEntryStatus } from "../../api/guards.ts";
import { parseDecimalString } from "../../helpers/parsedecimalString.ts";
import { serializeDate } from "../../helpers/date.ts";
import { Forwarding, ForwardingForm } from "../../types/forwarding.ts";
import { Contract, ContractForm } from "../../types/index.ts";
import { Delivery, DeliveryForm } from "../../types/delivery.ts";

export const transportOrderCreateToExternal = (
  entry: TransportOrdersForm,
): TransportOrderCreate | TransportOrderCreateWithSalesContract => ({
  loading_date: entry.loadingDate
    ? serializeDate(new Date(entry.loadingDate))
    : undefined,
  loaded_weight: entry.weightAtLoading ?? undefined,
  unloading_date: entry.unloadingDate
    ? serializeDate(new Date(entry.unloadingDate))
    : undefined,
  unloaded_weight: entry.weightAtLoading ?? undefined,
  entry_status: "default",
  // TODO: The form data is not of type TransportOrder, it needs to be a separate type
  // This is why all stuff here needs to be manually ast, as they aren't really strings
  product_id: entry.product ?? undefined,
  driver_id: entry.driver ?? undefined,
  truck_id: entry.trucks ?? undefined,
  trailer_id: entry.trailers ?? undefined,
  supplier_id: entry.seller ?? undefined,
  loading_location_id: entry.loadingLocation ?? undefined,
  purchase_price: entry.purchasePrice ?? undefined,
  purchase_invoice: entry.purchaseInvoice ?? undefined,
  purchase_contract_id: entry.purchaseContract ?? null,
  purchase_deductions: entry.purchaseDeductions ?? undefined,
  buyer_id: entry.buyer,
  unloading_location_id: entry.unloadingLocation,
  sales_price: entry.salesPrice ?? undefined,
  sales_invoice: entry.salesInvoice ?? undefined,
  sales_contract_id: entry.salesContract ?? undefined,
  sales_deductions: entry.salesDeduction ?? undefined,
  transporter_id: entry.carrier ?? undefined,
  transport_rate: entry.transportRate ?? undefined,
  marked_fields: [],
  count: entry.count,
});

export const transportOrderUpdateToExternal = (
  entry: Partial<TransportOrdersForm>,
): TransportOrderUpdate => ({
  loading_date: entry.loadingDate,
  loaded_weight: entry.weightAtLoading,
  unloading_date: entry.unloadingDate,
  unloaded_weight: entry.weightAtUnloading,
  ...(entry.entryStatus !== undefined && {
    entry_status: isEntryStatus(entry.entryStatus)
      ? entry.entryStatus
      : "default",
  }),
  product_id: entry.product,
  driver_id: entry.driver,
  truck_id: entry.trucks,
  trailer_id: entry.trailers,
  supplier_id: entry.seller,
  loading_location_id: entry.loadingLocation,
  purchase_price: entry.purchasePrice,
  purchase_invoice: entry.purchaseInvoice,
  purchase_contract_id: entry.purchaseContract,
  purchase_deductions: entry.purchaseDeductions,
  buyer_id: entry.buyer,
  unloading_location_id: entry.unloadingLocation,
  sales_price: entry.salesPrice,
  sales_invoice: entry.salesInvoice,
  sales_contract_id: entry.salesContract,
  sales_deductions: entry.salesDeduction,
  transporter_id: entry.carrier,
  transport_rate: entry.transportRate,
  marked_fields: entry.marked,
});

export const transportOrderFromExternalList = (
  entry: TransportOrderFromApi,
): TransportOrders => ({
  id: entry.id,
  deliveryStatus: entry.status,
  unloadingDate: entry.unloading_date ? new Date(entry.unloading_date) : null,
  loadingDate: entry.loading_date ? new Date(entry.loading_date) : null,
  weightAtLoading: parseDecimalString(entry.loaded_weight),
  weightAtUnloading: parseDecimalString(entry.unloaded_weight),
  driverId: entry.driver?.id ?? null,
  driver: entry.driver
    ? `${entry.driver.first_name} ${entry.driver.last_name}`
    : "",
  driverManager: entry.driver?.manager?.user?.email ?? "",
  trucks: entry.truck?.number ?? "",
  trailers: entry.trailer?.number ?? "",
  product: entry.product?.name ?? "",
  seller: entry.supplier?.name ?? "",
  loadingLocation: entry.loading_location?.address ?? "",
  loadingCity: entry.loading_location?.city ?? "",
  loadingCountry: entry.loading_location?.country ?? "",
  purchasePrice: parseDecimalString(entry.purchase_price) ?? 0,
  purchaseInvoice: entry.purchase_invoice ?? "",
  purchaseDeductions: entry.purchase_deductions ?? "",
  purchaseContract: entry.purchase_contract?.contract_number ?? "",
  buyer: entry.buyer.name,
  unloadingLocation: entry.unloading_location?.address ?? "",
  unloadingCity: entry.unloading_location?.city ?? "",
  unloadingCountry: entry.unloading_location?.country ?? "",
  salesPrice: parseDecimalString(entry.sales_price) ?? 0,
  salesInvoice: entry.sales_invoice ?? "",
  salesDeduction: entry.sales_deductions ?? "",
  salesContract: entry.sales_contract?.contract_number ?? "",
  carrier: entry.transporter?.name ?? "",
  transportRate: parseDecimalString(entry.transport_rate) ?? 0,
  merchant: "",
  entryStatus: entry.entry_status,
  marked: entry.marked_fields ?? [],
  readyForWaybiller: entry.ready_for_waybiller ?? false,
  fieldsNotReadyForWaybiller: entry.fields_not_ready_for_waybiller ?? {},
  number: entry.number ?? "",
  commentsCount: entry.comments_count,
});

export const forwardingOrderCreateToExternal = (
  entry: ForwardingForm,
): ForwardingOrderCreate => ({
  date: serializeDate(new Date(entry.date)),
  client_rate: entry.clientRate,
  sales_invoice: entry.salesInvoice ?? "",
  sales_order_number: entry.salesOrderNumber ?? "",
  weight: entry.weight,
  transport_rate: entry.transportRate ?? null,
  purchase_invoice: entry.purchaseInvoice ?? "",
  purchase_order_number: entry.purchaseOrderNumber ?? "",
  entry_status: "default",
  marked_fields: [],
  client_id: entry.buyer,
  product_id: entry.product,
  loading_location_id: entry.loadingLocation,
  transporter_id: entry.carrier ?? null,
  unloading_location_id: entry.unloadingLocation ?? null,
  driver_id: entry.driver ?? null,
  truck_id: entry.trucks ?? null,
  trailer_id: entry.trailers ?? null,
});

export const forwardingOrderUpdateToExternal = (
  entry: Partial<ForwardingForm>,
): ForwardingOrderUpdate => ({
  date: entry.date,
  client_rate: entry.clientRate,
  sales_invoice: entry.salesInvoice ?? "",
  weight: entry.weight,
  transport_rate: entry.transportRate,
  purchase_invoice: entry.purchaseInvoice ?? "",
  purchase_order_number: entry.purchaseOrderNumber ?? "",
  sales_order_number: entry.salesOrderNumber ?? "",
  ...(entry.entryStatus !== undefined && {
    entry_status: isEntryStatus(entry.entryStatus)
      ? entry.entryStatus
      : "default",
  }),
  marked_fields: entry.marked ?? [],
  client_id: entry.buyer,
  product_id: entry.product,
  transporter_id: entry.carrier,
  loading_location_id: entry.loadingLocation,
  unloading_location_id: entry.unloadingLocation,
  driver_id: entry.driver,
  truck_id: entry.trucks,
  trailer_id: entry.trailers,
});

export const forwardingOrderFromExternalList = (
  entry: ForwardingOrder,
): Forwarding => ({
  id: entry.id,
  date: new Date(entry.date),
  clientRate: Number(entry.client_rate),
  salesInvoice: entry.sales_invoice,
  weight: parseDecimalString(entry.weight),
  transportRate: parseDecimalString(entry.transport_rate),
  purchaseInvoice: entry.purchase_invoice,
  purchaseOrderNumber: entry.purchase_order_number ?? "",
  salesOrderNumber: entry.sales_order_number ?? "",
  entryStatus: entry.entry_status,
  marked: entry.marked_fields ?? [],
  deliveryStatus: entry.status,
  buyer: entry.client.name,
  product: entry.product?.name ?? "",
  carrier: entry.transporter?.name ?? "",
  driverId: entry.driver?.id ?? null,
  driver: entry.driver
    ? `${entry.driver.first_name} ${entry.driver.last_name}`
    : "",
  driverManager: entry.driver?.manager?.user?.email ?? "",
  trucks: entry.truck?.number ?? "",
  trailers: entry.trailer?.number ?? "",
  loadingLocation: entry.loading_location?.address ?? "",
  loadingCity: entry.loading_location?.city ?? "",
  loadingCountry: entry.loading_location?.country ?? "",
  unloadingLocation: entry.unloading_location?.address ?? "",
  unloadingCity: entry.unloading_location?.city ?? "",
  unloadingCountry: entry.unloading_location?.country ?? "",
  profit: parseDecimalString(entry.profit) ?? 0,
  readyForWaybiller: entry.ready_for_waybiller ?? false,
  fieldsNotReadyForWaybiller: entry.fields_not_ready_for_waybiller ?? {},
  number: entry.number ?? "",
  commentsCount: entry.comments_count,
});

export const contractCreateToExternal = (
  entry: ContractForm,
): ContractCreate => ({
  date: serializeDate(new Date(entry.date)),
  start_date: serializeDate(new Date(entry.startDate)),
  end_date: serializeDate(new Date(entry.endDate)),
  signed: entry.signed ?? false,
  contract_number: entry.contractNumber,
  payment_deadline_days: entry.paymentDeadlineDays ?? undefined,
  price: entry.price,
  weight: entry.weight ?? undefined,
  broker: entry.broker ?? undefined,
  broker_invoice: entry.brokerInvoice ?? undefined,
  product_id: entry.product,
  location_id: entry.location ?? undefined,
  client_id: entry.client,
  contract_type: entry.contractType,
  entry_status: "default",
  marked_fields: [],
});

export const contractUpdateToExternal = (
  entry: Partial<ContractForm>,
): ContractUpdate => ({
  date: entry.date,
  start_date: entry.startDate,
  end_date: entry.endDate,
  signed: entry.signed,
  contract_number: entry.contractNumber,
  payment_deadline_days: entry.paymentDeadlineDays,
  price: entry.price,
  weight: entry.weight,
  broker: entry.broker,
  broker_invoice: entry.brokerInvoice,
  product_id: entry.product,
  location_id: entry.location,
  client_id: entry.client,
  ...(entry.entryStatus !== undefined && {
    entry_status:
      isEntryStatus(entry.entryStatus) && entry.entryStatus !== "pending"
        ? entry.entryStatus
        : "default",
  }),
  marked_fields: entry.marked,
});

export const contractFromExternalList = (entry: ContractFromApi): Contract => ({
  id: entry.id,
  date: new Date(entry.date),
  startDate: new Date(entry.start_date),
  endDate: new Date(entry.end_date),
  signed: entry.signed,
  contractNumber: entry.contract_number,
  paymentDeadlineDays: entry.payment_deadline_days ?? null,
  price: parseDecimalString(entry.price) ?? 0,
  weight: parseDecimalString(entry.weight) ?? 0,
  client: entry.client.name,
  broker: entry.broker ?? "",
  brokerInvoice: entry.broker_invoice ?? "",
  product: entry.product?.name ?? "",
  location: entry.location?.address ?? "",
  country: entry.location?.country ?? "",
  city: entry.location?.city ?? "",
  entryStatus: entry.entry_status,
  marked: entry.marked_fields ?? [],
  deliveredWeight: entry.delivered_weight ?? "",
  remainingWeight: entry.remaining_weight ?? "",
  estimatedWeight: entry.estimated_weight ?? "",
  missingWeight: entry.missing_weight ?? "",
  contractType: entry.contract_type,
  canDownload: entry.has_contract_template,
  commentsCount: entry.comments_count,
  fileExists: entry.file_exists,
});

export const deliveryIdToInternal = ({
  id,
  source,
}: {
  id: number;
  source: "TransportOrder" | "ForwardingOrder";
}): number => (source === "TransportOrder" ? id : -id);
export const deliveryIdToExternal = (
  id: number,
): { id: number; source: "TransportOrder" | "ForwardingOrder" } => {
  if (id < 0) {
    return { id: -id, source: "ForwardingOrder" };
  }
  return { id, source: "TransportOrder" };
};

export const deliveryFromExternalList = (entry: DeliveryFromApi): Delivery => ({
  id: deliveryIdToInternal(entry),
  objectId: entry.id,
  source: entry.source,
  date: entry.date ? new Date(entry.date) : null,
  driver: entry.driver
    ? `${entry.driver.first_name} ${entry.driver.last_name}`
    : null,
  driverManager: entry.driver?.manager?.user?.email ?? null,
  loadingCountry: entry.loading_location?.country ?? "",
  loadingCity: entry.loading_location?.city ?? "",
  loadingAddress: entry.loading_location?.address ?? "",
  unloadingCountry: entry.unloading_location?.country ?? "",
  unloadingCity: entry.unloading_location?.city ?? "",
  unloadingAddress: entry.unloading_location?.address ?? "",
  transportRate: parseDecimalString(entry.transport_rate),
  deliveryStatus: entry.status,
  entryStatus: entry.entry_status,
  marked: entry.marked_fields ?? [],
  sortRank: entry.sort_rank,
  commentsCount: entry.comments_count,
});

export const deliveryUpdateToExternal = (
  entry: Partial<DeliveryForm>,
): DeliveryUpdate => ({
  date: entry.date,
  transport_rate: entry.transportRate,
  ...(entry.entryStatus !== undefined && {
    entry_status: isEntryStatus(entry.entryStatus)
      ? entry.entryStatus
      : "default",
  }),
  marked_fields: entry.marked,
  sort_rank: entry.sortRank,
});

/*export const goodsToTakeAwayCreateToExternal = (
  entry: GoodsToTakeAway
): GoodsToTakeAwayCreate => ({
  ...entry,
});

export const goodsToTakeAwayUpdateToExternal = (
  entry: Partial<GoodsToTakeAway>
): GoodsToTakeAwayUpdate => ({
  ...entry,
});

export const goodsToTakeAwayFromExternalList = (
  entry: GoodsToTakeAway
): GoodsToTakeAway => ({
  ...entry,
});*/
